import { useNavigate } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useRef } from 'react';
import { match } from 'ts-pattern';

import { Icons } from '@/assets/icons/icons';
import { AnimatedAudiowave, Button, Tooltip, XStack } from '@/components/ui';
import { useAudioWaveSilenceDetection } from '@/components/ui/AnimatedAudiowave';
import { Rail } from '@/design-system-v2/rail';
import { useViewportSize } from '@/hooks/useViewportSize';
import { EASE, withUIDelay } from '@/lib/animate';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';

import { meetingApis } from './meetings.query';
import { meetingStore } from './meetings.state';
import { type Meeting, type MeetingAudioEnergyLevel } from './meetings.types';

/** Collapse the recording control to an icon-only pill below this content width. */
export const COMPACT_CONTROL_BELOW_CONTENT_PX = 768;

/**
 * Pill component that appears in the sidebar footer when a meeting is being recorded.
 * Shows audio levels and provides quick navigation back to the active meeting note.
 */
export const ActiveMeetingPill = observer(
  ({ variant = 'pill' }: { variant?: 'pill' | 'rail' }) => {
    const { recording } = meetingStore;
    const meetingInRecord =
      meetingStore.asRecordingOrStoppingMeeting?.meetingId;
    const navigate = useNavigate();
    const shouldReduceMotion = useReducedMotion();
    const isViewingRecordingMeeting = meetingStore.isMeetingIdRecording(
      meetingStore.meetingInViewId
    );
    const show =
      recording.status === 'recording' &&
      !!meetingInRecord &&
      !isViewingRecordingMeeting;

    const updateWaveRef = useRef<(levels: MeetingAudioEnergyLevel) => void>(
      () => null
    );

    useEffect(() => {
      if (!show) return;

      const unsubscribe = meetingStore.subscribeAudioEnergyLevel(
        (newLevels) => {
          updateWaveRef.current?.(newLevels);
        }
      );

      return unsubscribe;
    }, [show]);

    const handleClick = useCallback(() => {
      if (!meetingInRecord) return;

      navigate({
        to: '/meetings/$meetingId',
        params: { meetingId: meetingInRecord },
        search: { tab: 'notes' },
      });
    }, [meetingInRecord, navigate]);

    if (!show) return null;

    if (variant === 'rail') {
      return (
        <Rail.Button
          label="Meeting in progress"
          caption="Live"
          className={cn(
            'text-brand-cyan-cornflower-blue hover:text-brand-cyan-cornflower-blue',
            'dark:text-brand-jordy-blue dark:hover:text-brand-jordy-blue',
            '[&>span:first-child]:bg-brand-jordy-blueA/20 dark:[&>span:first-child]:bg-brand-jordy-blueA/10',
            '[&:hover>span:first-child]:bg-brand-jordy-blueA/30 dark:[&:hover>span:first-child]:bg-brand-jordy-blueA/20'
          )}
          onClick={handleClick}
        >
          <AnimatedAudiowave
            updateWaveRef={updateWaveRef}
            maxLength={16}
            barColor="bg-brand-cyan-cornflower-blue dark:bg-brand-jordy-blue"
            gap="2px"
            barWidth="2px"
          />
        </Rail.Button>
      );
    }

    // No exit animation: an exit that lands while a Suspense fallback hides the
    // shell never starts, and `AnimatePresence` then keeps the pill mounted
    return (
      <motion.button
        type="button"
        initial={shouldReduceMotion ? false : { opacity: 0, y: 4, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.12,
          ease: 'easeOut',
        }}
        onClick={handleClick}
        className={cn(
          'flex items-center',
          'w-full gap-1',
          'text-sm text-foreground-subtle',
          'border border-brand-jordy-blueA/40 dark:border-brand-jordy-blueA/15',
          'p-[3px]',
          'rounded-xl',
          'bg-brand-jordy-blueA/10',
          'hover:bg-brand-jordy-blueA/20',
          'transition-colors'
        )}
      >
        <XStack className="flex-1 min-w-0 h-8 items-center gap-1 pl-[2px] pr-2">
          <XStack center className="size-6 shrink-0">
            <Icons.mic className="size-3.5 text-brand-cyan-cornflower-blue dark:text-brand-jordy-blue" />
          </XStack>
          <span className="flex-1 min-w-0 truncate text-left">
            Meeting in progress
          </span>
        </XStack>
        <div className="flex items-center justify-center size-8">
          <AnimatedAudiowave
            updateWaveRef={updateWaveRef}
            maxLength={20}
            barColor="bg-brand-cyan-cornflower-blue dark:bg-brand-jordy-blue"
            gap="0.15rem"
            barWidth="0.15rem"
          />
        </div>
      </motion.button>
    );
  }
);

ActiveMeetingPill.displayName = 'ActiveMeetingPill';

interface MeetingControlBarProps {
  meeting: Meeting;
}

/** Recording-control pill (waveform + Stop/Start/Resume); positioned by `MeetingChatDock`. */
export const MeetingControlBar = observer(
  ({ meeting }: MeetingControlBarProps) => {
    const meetingId = meeting.id;
    const { isRecordingThisMeeting, isAnotherMeetingRecording } = match(
      meetingStore.recording
    )
      .with({ status: 'recording' }, ({ meetingId: recordedMeetingId }) => {
        const recordingThis = recordedMeetingId === meetingId;
        return {
          isRecordingThisMeeting: recordingThis,
          isAnotherMeetingRecording: !recordingThis,
        };
      })
      .otherwise(() => ({
        isRecordingThisMeeting: false,
        isAnotherMeetingRecording: false,
      }));

    const isPrepState = meetingStore.isPrepState(meeting.id);
    const isLoading = meetingStore.controlBarLoading;
    const shouldReduceMotion = useReducedMotion();
    const { width: viewportWidth } = useViewportSize();
    // Measure against content width, not viewport, so compact kicks in
    // sooner when the left sidebar is taking a column.
    const leftSidebarColumn = uiStore.isSidebarHidden
      ? 0
      : uiStore.sidebarWidth;
    const contentWidth = viewportWidth - leftSidebarColumn;
    const compactControl =
      viewportWidth > 0 && contentWidth < COMPACT_CONTROL_BELOW_CONTENT_PX;

    const subscribeEnergyLevel = useCallback(
      (cb: (levels: MeetingAudioEnergyLevel) => void) => {
        if (!isRecordingThisMeeting) return () => {};
        return meetingStore.subscribeAudioEnergyLevel(cb);
      },
      [isRecordingThisMeeting]
    );

    const { updateWaveRef, barColor: resolvedBarColor } =
      useAudioWaveSilenceDetection(
        subscribeEnergyLevel,
        'bg-green-600 [.system-dark_&]:bg-green-400'
      );

    const handleStop = useCallback(() => {
      meetingStore.controlBarLoading = true;
      withUIDelay(async () => {
        try {
          meetingStore.closeTranscriptPopover();
          await meetingApis.enqueueStopMeeting(
            { type: 'stop_note_taking' },
            { deferSummary: true }
          );
        } finally {
          meetingStore.controlBarLoading = false;
        }
      });
    }, []);

    const handleStart = useCallback(() => {
      meetingStore.controlBarLoading = true;
      withUIDelay(async () => {
        try {
          await meetingApis.enqueueStartMeeting({
            payload: {
              meetingId,
              startReason: 'resume',
            },
          });
        } finally {
          meetingStore.controlBarLoading = false;
        }
      });
    }, [meetingId]);

    const handleResume = useCallback(() => {
      meetingStore.controlBarLoading = true;
      withUIDelay(async () => {
        try {
          await meetingApis.enqueueStartMeeting({
            payload: {
              meetingId,
              startReason: 'resume',
              resumeSource: 'meeting_page',
            },
          });
        } finally {
          meetingStore.controlBarLoading = false;
        }
      });
    }, [meetingId]);

    return (
      <motion.div
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.18,
          ease: EASE.outQuint,
        }}
        className={cn(
          'pointer-events-auto',
          'flex items-center gap-1',
          'p-2',
          'rounded-2xl',
          'bg-surface',
          'border border-border-strong',
          'shadow-sm dark:shadow-lg'
        )}
        data-testid={
          isRecordingThisMeeting ? 'meeting_recording_indicator' : undefined
        }
      >
        {/* Live waveform (or loading wave during start/stop transitions). */}
        <div
          role="img"
          aria-label={
            isLoading
              ? 'Loading'
              : isRecordingThisMeeting
                ? 'Live audio levels'
                : 'Audio indicator'
          }
          className="flex items-center justify-center h-10 pl-2 pr-2.5"
        >
          {isLoading ? (
            <LoadingWaveform />
          ) : (
            <AnimatedAudiowave
              idle={!isRecordingThisMeeting}
              updateWaveRef={updateWaveRef}
              maxLength={22}
              barColor={
                isRecordingThisMeeting
                  ? resolvedBarColor
                  : 'bg-foreground-subtle'
              }
              gap="3.5px"
              barWidth="2.5px"
            />
          )}
        </div>

        {/* Stop / Start now / Resume — one Button + one Tooltip whose
              props swap, so React reconciles the DOM in place rather than
              unmounting and remounting on every state transition. */}
        {(() => {
          const mode = isRecordingThisMeeting
            ? 'stop'
            : isPrepState
              ? 'start'
              : 'resume';
          const isStop = mode === 'stop';
          const blockedByOther = !isStop && isAnotherMeetingRecording;
          const tooltipLabel = isStop
            ? 'Stop transcribing'
            : blockedByOther
              ? 'Another meeting is recording'
              : mode === 'start'
                ? 'Start recording'
                : 'Resume recording';
          const buttonLabel =
            mode === 'stop'
              ? 'Stop'
              : mode === 'start'
                ? 'Start now'
                : 'Resume';
          const Icon = isStop ? Icons.stop : Icons.play;
          const onClick =
            mode === 'stop'
              ? handleStop
              : mode === 'start'
                ? handleStart
                : handleResume;
          const testId =
            mode === 'stop'
              ? 'meeting_stop_recording'
              : mode === 'start'
                ? 'meeting_start_recording'
                : 'meeting_resume_recording';

          return (
            <Tooltip
              label={tooltipLabel}
              hotkey={!blockedByOther ? 'meetings.toggle' : undefined}
            >
              <Button
                size="default"
                variant="secondary"
                onClick={onClick}
                aria-label={compactControl || isStop ? tooltipLabel : undefined}
                disabled={isLoading || blockedByOther}
                className={cn(
                  'relative h-10',
                  compactControl ? 'w-10' : 'w-28'
                )}
                data-testid={testId}
              >
                {/* Crossfade icon + label when the mode changes so the
                      button doesn't snap between Stop / Start now / Resume.
                      Both spans are absolutely centered so their differing
                      content widths can't shift sideways during the fade. */}
                <AnimatePresence initial={false}>
                  <motion.span
                    key={mode}
                    initial={shouldReduceMotion ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: shouldReduceMotion ? 0 : 0.1,
                      ease: 'linear',
                    }}
                    className="absolute inset-0 flex items-center justify-center gap-2"
                  >
                    <Icon className="size-4" />
                    {!compactControl && buttonLabel}
                  </motion.span>
                </AnimatePresence>
              </Button>
            </Tooltip>
          );
        })()}
      </motion.div>
    );
  }
);

MeetingControlBar.displayName = 'MeetingControlBar';

/**
 * Three-bar waveform that pulses in a wave pattern. Shown in place of the
 * live audio meter while a meeting start / stop transition is in flight.
 * Pure CSS — keyframes in `apps/desktop/src/styles/base.css`.
 */
function LoadingWaveform() {
  return (
    <div
      aria-hidden
      data-testid="meeting_loading_wave"
      className="flex w-[22px] items-center justify-center gap-[3.5px]"
    >
      <span className="loading-wave-bar" style={{ animationDelay: '0ms' }} />
      <span className="loading-wave-bar" style={{ animationDelay: '150ms' }} />
      <span className="loading-wave-bar" style={{ animationDelay: '300ms' }} />
    </div>
  );
}
